import React, { useState, useEffect } from 'react';

interface TrendData {
  category: string;
  subcategory: string;
  value: number;
  previousValue: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  timeframe: string;
  significance: 'high' | 'medium' | 'low';
}

interface MarketTrend {
  id: string;
  name: string;
  category: 'strain_popularity' | 'product_types' | 'effects_demand' | 'geographic' | 'pricing' | 'seasonal';
  description: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number; // 0-100
  dataPoints: Array<{
    date: string;
    value: number;
    label?: string;
  }>;
  insights: string[];
  predictions: Array<{
    timeframe: string;
    prediction: string;
    confidence: number;
  }>;
}

interface SeasonalPattern {
  period: string;
  pattern: 'peak' | 'valley' | 'stable';
  description: string;
  averageChange: number;
  historicalData: Array<{
    year: number;
    value: number;
  }>;
}

interface GeographicTrend {
  region: string;
  growth: number;
  marketShare: number;
  topStrains: string[];
  characteristics: string[];
}

interface PlatformTrendsData {
  overview: {
    totalTrends: number;
    emergingTrends: number;
    peakTrends: number;
    decliningTrends: number;
  };
  strainTrends: TrendData[];
  effectsTrends: TrendData[];
  marketTrends: MarketTrend[];
  seasonalPatterns: SeasonalPattern[];
  geographicTrends: GeographicTrend[];
  userBehavior: {
    searchPatterns: Array<{
      term: string;
      searches: number;
      growth: number;
    }>;
    purchasePatterns: Array<{
      pattern: string;
      percentage: number;
      change: number;
    }>;
    demographicShifts: Array<{
      demographic: string;
      change: number;
      significance: string;
    }>;
  };
}

interface PlatformTrendsAnalysisProps {
  timeframe: '30d' | '90d' | '1y' | 'all';
  userRole: 'admin' | 'staff' | 'vendor';
}

export default function PlatformTrendsAnalysis({ timeframe = '90d', userRole }: PlatformTrendsAnalysisProps) {
  const [trendsData, setTrendsData] = useState<PlatformTrendsData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'strains' | 'effects' | 'market' | 'seasonal' | 'geographic' | 'behavior'>('strains');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrendsData = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockStrainTrends: TrendData[] = [
          {
            category: 'Red Strains',
            subcategory: 'Red Maeng Da',
            value: 2340,
            previousValue: 1850,
            change: 490,
            changePercent: 26.5,
            trend: 'up',
            timeframe: timeframe,
            significance: 'high'
          },
          {
            category: 'Red Strains',
            subcategory: 'Red Bali',
            value: 1890,
            previousValue: 1650,
            change: 240,
            changePercent: 14.5,
            trend: 'up',
            timeframe: timeframe,
            significance: 'medium'
          },
          {
            category: 'Green Strains',
            subcategory: 'Green Maeng Da',
            value: 1650,
            previousValue: 1920,
            change: -270,
            changePercent: -14.1,
            trend: 'down',
            timeframe: timeframe,
            significance: 'medium'
          },
          {
            category: 'White Strains',
            subcategory: 'White Borneo',
            value: 1240,
            previousValue: 890,
            change: 350,
            changePercent: 39.3,
            trend: 'up',
            timeframe: timeframe,
            significance: 'high'
          },
          {
            category: 'Gold/Yellow',
            subcategory: 'Gold Maeng Da',
            value: 890,
            previousValue: 1120,
            change: -230,
            changePercent: -20.5,
            trend: 'down',
            timeframe: timeframe,
            significance: 'medium'
          }
        ];

        const mockEffectsTrends: TrendData[] = [
          {
            category: 'Primary Effects',
            subcategory: 'Pain Relief',
            value: 3450,
            previousValue: 2890,
            change: 560,
            changePercent: 19.4,
            trend: 'up',
            timeframe: timeframe,
            significance: 'high'
          },
          {
            category: 'Primary Effects',
            subcategory: 'Energy/Focus',
            value: 2340,
            previousValue: 2580,
            change: -240,
            changePercent: -9.3,
            trend: 'down',
            timeframe: timeframe,
            significance: 'medium'
          },
          {
            category: 'Primary Effects',
            subcategory: 'Sleep Aid',
            value: 1890,
            previousValue: 1650,
            change: 240,
            changePercent: 14.5,
            trend: 'up',
            timeframe: timeframe,
            significance: 'medium'
          },
          {
            category: 'Primary Effects',
            subcategory: 'Anxiety Relief',
            value: 1650,
            previousValue: 1420,
            change: 230,
            changePercent: 16.2,
            trend: 'up',
            timeframe: timeframe,
            significance: 'medium'
          }
        ];

        const mockMarketTrends: MarketTrend[] = [
          {
            id: '1',
            name: 'Red Strain Renaissance',
            category: 'strain_popularity',
            description: 'Red strains experiencing unprecedented growth driven by pain relief and sleep aid demand.',
            impact: 'high',
            confidence: 87,
            dataPoints: [
              { date: '2023-10', value: 1200 },
              { date: '2023-11', value: 1450 },
              { date: '2023-12', value: 1680 },
              { date: '2024-01', value: 2100 }
            ],
            insights: [
              'Red Maeng Da leading with 45% growth',
              'Evening consumption patterns driving demand',
              'Correlation with increased stress levels in user base'
            ],
            predictions: [
              {
                timeframe: 'Next 3 months',
                prediction: 'Red strain demand will continue growing 15-20% monthly',
                confidence: 82
              },
              {
                timeframe: 'Next 6 months',
                prediction: 'Red strains will represent 40% of total market share',
                confidence: 74
              }
            ]
          },
          {
            id: '2',
            name: 'Premium Extract Growth',
            category: 'product_types',
            description: 'Extract products showing strong growth as users seek higher potency options.',
            impact: 'medium',
            confidence: 78,
            dataPoints: [
              { date: '2023-10', value: 340 },
              { date: '2023-11', value: 425 },
              { date: '2023-12', value: 520 },
              { date: '2024-01', value: 680 }
            ],
            insights: [
              'Extract sales up 67% in last quarter',
              'Higher profit margins attracting vendor interest',
              'Quality concerns driving demand for lab-tested extracts'
            ],
            predictions: [
              {
                timeframe: 'Next 3 months',
                prediction: 'Extract market will grow 25% but may face regulatory scrutiny',
                confidence: 71
              }
            ]
          },
          {
            id: '3',
            name: 'Geographic Expansion',
            category: 'geographic',
            description: 'Significant growth in previously underserved geographic markets.',
            impact: 'high',
            confidence: 91,
            dataPoints: [
              { date: '2023-10', value: 2400 },
              { date: '2023-11', value: 2890 },
              { date: '2023-12', value: 3340 },
              { date: '2024-01', value: 4120 }
            ],
            insights: [
              'Southern and Midwest regions showing 40%+ growth',
              'Rural areas adopting kratom for pain management',
              'State legislation changes opening new markets'
            ],
            predictions: [
              {
                timeframe: 'Next 6 months',
                prediction: 'Three additional states will see significant market growth',
                confidence: 85
              }
            ]
          }
        ];

        const mockSeasonalPatterns: SeasonalPattern[] = [
          {
            period: 'Winter (Dec-Feb)',
            pattern: 'peak',
            description: 'Peak season for red strains and sleep aids due to SAD and holiday stress',
            averageChange: 23.5,
            historicalData: [
              { year: 2021, value: 15.2 },
              { year: 2022, value: 19.8 },
              { year: 2023, value: 23.5 }
            ]
          },
          {
            period: 'Spring (Mar-May)',
            pattern: 'stable',
            description: 'Stable demand with slight shift toward green strains for energy',
            averageChange: -2.1,
            historicalData: [
              { year: 2021, value: -5.4 },
              { year: 2022, value: -1.2 },
              { year: 2023, value: -2.1 }
            ]
          },
          {
            period: 'Summer (Jun-Aug)',
            pattern: 'valley',
            description: 'Lowest demand period with increased white strain preference',
            averageChange: -12.8,
            historicalData: [
              { year: 2021, value: -16.2 },
              { year: 2022, value: -11.4 },
              { year: 2023, value: -12.8 }
            ]
          },
          {
            period: 'Fall (Sep-Nov)',
            pattern: 'peak',
            description: 'Recovery period with balanced strain preferences',
            averageChange: 8.4,
            historicalData: [
              { year: 2021, value: 6.7 },
              { year: 2022, value: 9.1 },
              { year: 2023, value: 8.4 }
            ]
          }
        ];

        const mockGeographicTrends: GeographicTrend[] = [
          {
            region: 'Southeast',
            growth: 34.2,
            marketShare: 28.5,
            topStrains: ['Red Maeng Da', 'Green Bali', 'White Thai'],
            characteristics: ['Pain management focus', 'Value-conscious buyers', 'Brand loyalty']
          },
          {
            region: 'West Coast',
            growth: 18.7,
            marketShare: 32.1,
            topStrains: ['Green Maeng Da', 'White Borneo', 'Gold varieties'],
            characteristics: ['Quality-focused', 'Premium product preference', 'Lab result conscious']
          },
          {
            region: 'Northeast',
            growth: 22.3,
            marketShare: 19.8,
            topStrains: ['Red Bali', 'Green Thai', 'White Maeng Da'],
            characteristics: ['Professional users', 'Evening consumption', 'Stress management']
          },
          {
            region: 'Midwest',
            growth: 41.6,
            marketShare: 15.2,
            topStrains: ['Red varieties', 'Traditional strains', 'Powder preference'],
            characteristics: ['Emerging market', 'Price-sensitive', 'Word-of-mouth driven']
          }
        ];

        const mockData: PlatformTrendsData = {
          overview: {
            totalTrends: 24,
            emergingTrends: 8,
            peakTrends: 12,
            decliningTrends: 4
          },
          strainTrends: mockStrainTrends,
          effectsTrends: mockEffectsTrends,
          marketTrends: mockMarketTrends,
          seasonalPatterns: mockSeasonalPatterns,
          geographicTrends: mockGeographicTrends,
          userBehavior: {
            searchPatterns: [
              { term: 'red maeng da pain relief', searches: 12400, growth: 45.2 },
              { term: 'kratom sleep aid', searches: 8900, growth: 32.1 },
              { term: 'white borneo energy', searches: 6750, growth: 67.8 },
              { term: 'lab tested kratom', searches: 5600, growth: 28.4 }
            ],
            purchasePatterns: [
              { pattern: 'Bulk buying (>100g)', percentage: 34.5, change: 12.3 },
              { pattern: 'Sample packs first', percentage: 28.7, change: 8.9 },
              { pattern: 'Brand switching', percentage: 22.1, change: -5.2 },
              { pattern: 'Repeat customers', percentage: 67.8, change: 15.4 }
            ],
            demographicShifts: [
              { demographic: 'Ages 35-50', change: 23.4, significance: 'Growing primary user base' },
              { demographic: 'Rural areas', change: 41.2, significance: 'Expanding geographic reach' },
              { demographic: 'Healthcare workers', change: 28.9, significance: 'Professional adoption' }
            ]
          }
        };

        setTrendsData(mockData);
        setLoading(false);
      }, 1200);
    };

    fetchTrendsData();
  }, [timeframe]);

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return '#10b981';
      case 'down': return '#ef4444';
      case 'stable': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      case 'stable': return '➡️';
      default: return '📊';
    }
  };

  const getSignificanceColor = (significance: string) => {
    switch (significance) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPatternIcon = (pattern: string) => {
    switch (pattern) {
      case 'peak': return '⛰️';
      case 'valley': return '🏔️';
      case 'stable': return '📊';
      default: return '📈';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Analyzing platform trends...</p>
      </div>
    );
  }

  if (!trendsData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Trends data not available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Platform Trends Analysis
        </h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          Market insights and trend analysis for the kratom industry ({timeframe})
        </p>
      </div>

      {/* Overview Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '3rem' 
      }}>
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.5rem' }}>
            {trendsData.overview.totalTrends}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Trends Tracked</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.5rem' }}>
            {trendsData.overview.emergingTrends}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Emerging Trends</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f59e0b', marginBottom: '0.5rem' }}>
            {trendsData.overview.peakTrends}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Peak Trends</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '0.5rem' }}>
            {trendsData.overview.decliningTrends}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Declining Trends</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem', overflowX: 'auto' }}>
            {(['strains', 'effects', 'market', 'seasonal', 'geographic', 'behavior'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setSelectedCategory(tab)}
                style={{
                  padding: '1rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: selectedCategory === tab ? '3px solid #3b82f6' : '3px solid transparent',
                  color: selectedCategory === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: selectedCategory === tab ? '600' : '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab === 'behavior' ? 'User Behavior' : tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {selectedCategory === 'strains' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Strain Popularity Trends
          </h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {trendsData.strainTrends.map((strain, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '1.5rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.5rem',
                border: '1px solid #4a5568'
              }}>
                <div style={{ flex: '1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {strain.subcategory}
                    </h4>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getSignificanceColor(strain.significance) + '20',
                      color: getSignificanceColor(strain.significance),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {strain.significance}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {strain.category} • {strain.value.toLocaleString()} searches
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '1.25rem', 
                      fontWeight: 'bold', 
                      color: getTrendColor(strain.trend),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {strain.changePercent > 0 ? '+' : ''}{strain.changePercent.toFixed(1)}%
                      <span style={{ fontSize: '1rem' }}>{getTrendIcon(strain.trend)}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {strain.change > 0 ? '+' : ''}{strain.change.toLocaleString()} change
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCategory === 'market' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {trendsData.marketTrends.map(trend => (
            <div key={trend.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div style={{ flex: '1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {trend.name}
                    </h3>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getImpactColor(trend.impact) + '20',
                      color: getImpactColor(trend.impact),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {trend.impact} Impact
                    </span>
                  </div>
                  <p style={{ fontSize: '1rem', color: '#a0aec0', margin: '0 0 1rem 0', lineHeight: '1.6' }}>
                    {trend.description}
                  </p>
                </div>
                
                <div style={{ textAlign: 'right', minWidth: '120px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.25rem' }}>
                    {trend.confidence}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Confidence</div>
                </div>
              </div>

              {/* Chart */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
                  Trend Data
                </h4>
                <div style={{ height: '150px', display: 'flex', alignItems: 'end', gap: '1rem', padding: '1rem 0' }}>
                  {trend.dataPoints.map((point, index) => (
                    <div key={index} style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ 
                        width: '100%', 
                        backgroundColor: '#3b82f6',
                        borderRadius: '4px 4px 0 0',
                        height: `${(point.value / Math.max(...trend.dataPoints.map(p => p.value))) * 100}px`,
                        minHeight: '4px',
                        transition: 'height 0.3s ease'
                      }}></div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                        {point.date}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#e2e8f0', fontWeight: '500' }}>
                        {point.value.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', color: '#e2e8f0' }}>
                  Key Insights
                </h4>
                <ul style={{ margin: '0', paddingLeft: '1.5rem', color: '#a0aec0' }}>
                  {trend.insights.map((insight, index) => (
                    <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Predictions */}
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', color: '#e2e8f0' }}>
                  Predictions
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {trend.predictions.map((prediction, index) => (
                    <div key={index} style={{ 
                      padding: '1rem', 
                      backgroundColor: '#eff6ff', 
                      borderRadius: '0.375rem',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af' }}>
                          {prediction.timeframe}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#1e40af' }}>
                          {prediction.confidence}% confidence
                        </span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: '0' }}>
                        {prediction.prediction}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedCategory === 'seasonal' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Seasonal Patterns
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {trendsData.seasonalPatterns.map((pattern, index) => (
              <div key={index} style={{ 
                padding: '1.5rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.5rem',
                border: '1px solid #4a5568'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '2rem' }}>{getPatternIcon(pattern.pattern)}</span>
                  <div>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.25rem 0' }}>
                      {pattern.period}
                    </h4>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: pattern.pattern === 'peak' ? '#10b98120' : pattern.pattern === 'valley' ? '#ef444420' : '#6b728020',
                      color: pattern.pattern === 'peak' ? '#10b981' : pattern.pattern === 'valley' ? '#ef4444' : '#6b7280',
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {pattern.pattern}
                    </span>
                  </div>
                </div>
                
                <p style={{ fontSize: '0.875rem', color: '#a0aec0', marginBottom: '1rem', lineHeight: '1.5' }}>
                  {pattern.description}
                </p>
                
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Average Change</span>
                    <span style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: pattern.averageChange > 0 ? '#10b981' : '#ef4444' 
                    }}>
                      {pattern.averageChange > 0 ? '+' : ''}{pattern.averageChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                    Historical Data
                  </div>
                  {pattern.historicalData.map((data, dataIndex) => (
                    <div key={dataIndex} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      <span style={{ color: '#6b7280' }}>{data.year}</span>
                      <span style={{ color: data.value > 0 ? '#10b981' : '#ef4444', fontWeight: '500' }}>
                        {data.value > 0 ? '+' : ''}{data.value.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCategory === 'geographic' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Geographic Market Trends
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {trendsData.geographicTrends.map((region, index) => (
              <div key={index} style={{ 
                padding: '1.5rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.5rem',
                border: '1px solid #4a5568'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>
                    {region.region}
                  </h4>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Growth: </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                        +{region.growth.toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Market Share: </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#3b82f6' }}>
                        {region.marketShare.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                    Top Strains
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {region.topStrains.map((strain, strainIndex) => (
                      <span key={strainIndex} style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: '#3b82f620',
                        color: '#3b82f6',
                        borderRadius: '0.25rem'
                      }}>
                        {strain}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                    Market Characteristics
                  </div>
                  <ul style={{ margin: '0', paddingLeft: '1rem', color: '#a0aec0' }}>
                    {region.characteristics.map((characteristic, charIndex) => (
                      <li key={charIndex} style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                        {characteristic}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCategory === 'behavior' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
          {/* Search Patterns */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Top Search Patterns
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {trendsData.userBehavior.searchPatterns.map((pattern, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.375rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem' }}>
                      "{pattern.term}"
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {pattern.searches.toLocaleString()} searches
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    color: '#10b981',
                    textAlign: 'right'
                  }}>
                    +{pattern.growth.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Purchase Patterns */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Purchase Behavior
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {trendsData.userBehavior.purchasePatterns.map((pattern, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.375rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem' }}>
                      {pattern.pattern}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {pattern.percentage.toFixed(1)}% of users
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    color: pattern.change > 0 ? '#10b981' : '#ef4444',
                    textAlign: 'right'
                  }}>
                    {pattern.change > 0 ? '+' : ''}{pattern.change.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Demographic Shifts */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)', gridColumn: 'span 2' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Demographic Shifts
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {trendsData.userBehavior.demographicShifts.map((shift, index) => (
                <div key={index} style={{ 
                  padding: '1.5rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.5rem',
                  border: '1px solid #4a5568'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {shift.demographic}
                    </h4>
                    <span style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: '#10b981'
                    }}>
                      +{shift.change.toFixed(1)}%
                    </span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0' }}>
                    {shift.significance}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}